<template>
  <div style="width: 100%">
    <v-card-title v-if="searchable">
      <v-spacer />
      <v-text-field
        :value="searchFromStore"
        @input="updateSearchInStore"
        append-icon="search"
        :label="$t('Search')"
        :placeholder="$t('Search')"
        class="data-table-search"
        single-line
        hide-details
      />
    </v-card-title>

    <v-data-table
      :headers="headers"
      :items="getItems"
      :search="searchFromStore"
      :loading="loading"
      loading-text=""
      :items-per-page-text="rowsPerPageText"
      :items-per-page-items="rowsPerPageItems"
      :header-props="{ sortIcon }"
      :footer-props="{ prevIcon, nextIcon,'items-per-page-options':rowsPerPageItems }"
      :page.sync="pagination.page"
      :items-per-page.sync="paginationListItem.rowsPerPage"
      @pagination="setRowsOnLocalStorage(dataTableListItemId,paginationListItem.rowsPerPage)"
    >
      <template v-slot:item="props">
        <tr>
          <td>{{ props.item.code }}</td>

          <td>{{ props.item.libelle }}</td>

          <td v-if="!isCustomList">{{ props.item.client_code }}</td>

          <td v-if="!isCustomList">{{ props.item.client_label }}</td>

          <td v-if="!isCustomList">
            <v-layout v-if="!props.item.blocked" row align-center>
              <div class="company-status-icon mr-3" />
              <span style="text-transform: lowercase">
                {{ $t("No") }}
              </span>
            </v-layout>

            <v-layout v-else row align-center>
              <div class="company-status-icon status-inactive mr-3" />
              <span style="text-transform: lowercase">
                {{ $t("Yes") }}
              </span>
            </v-layout>
          </td>

          <td v-if="!isCustomList">
            <v-layout v-if="!props.item.deleted" row align-center>
              <div class="company-status-icon mr-3" />
              <span style="text-transform: lowercase">
                {{ $t("No") }}
              </span>
            </v-layout>

            <v-layout v-else row align-center>
              <div class="company-status-icon status-inactive mr-3" />
              <span style="text-transform: lowercase">
                {{ $t("Yes") }}
              </span>
            </v-layout>
          </td>

          <td v-if="!isCustomList">{{ props.item.definition }}</td>

          <td>{{ props.item.index }}</td>

          <td>
            <app-datatable-action-buttons
              :user-can-edit="
                isSuperAdmin ||
                  (userHasManagementPermission('list_item', 'update') &&
                    isAdmin &&
                    !isCustomList)
              "
              @edit="editItem(props.item)"
              :user-can-delete="isCustomList ? true : !props.item.blocked"
              @delete="askConfirmationToDeleteItem(props.item)"
            />
          </td>
        </tr>
      </template>

      <template v-slot:pageText="props">
        {{ props.pageStart }} - {{ props.pageStop }} {{ $t("tables.Of") }}
        {{ props.itemsLength }}
      </template>

      <template v-slot:no-results>
        <data-table-no-results :search="searchFromParent" />
      </template>

      <template v-slot:no-data>
        <div v-if="loading" />
      </template>
    </v-data-table>

    <app-dialog-box
      :open="deleteDialog"
      :title="$t('DeleteItem')"
      :content="
        $t('DeleteConfirmation') +
          ' ' +
          (itemToDelete.client_label
            ? itemToDelete.client_label
            : itemToDelete.libelle + '?')
      "
      :main-action-label="$t('No')"
      @mainAction="cancelDelete"
      :secondary-action-label="$t('Yes')"
      @secondaryAction="deleteItem"
      :loading="loadingDelete"
    />
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { managementDatatableMixin } from "@/mixins/management/managementDatatableMixin";
import DataTableNoResults from "@/components/_Atoms/DataTableNoResults";
import AppDialogBox from "@/components/_Molecules/AppDialogBox";
import AppDatatableActionButtons from "@/components/_Molecules/AppDatatableActionButtons";
import { checkPermissionsMixin } from "@/mixins/checkPermissionsMixin";

export default {
  name: "ListItemsDataTable",

  components: {
    AppDatatableActionButtons,
    DataTableNoResults,
    AppDialogBox
  },

  mixins: [managementDatatableMixin, checkPermissionsMixin],

  props: {
    /**
     * The uuid of the list to which the items list belongs
     */
    listUuid: {
      type: String
    },

    /**
     * Indicates if the list to which the items list belongs is custom or not
     */
    isCustomList: {
      type: Boolean
    },

    searchable: {
      type: Boolean,
      required: false
    },

    /**
     * List of new list items for the new list
     */
    newListItems: {
      type: Array
    }
  },

  data() {
    return {
      /**
       * Data of the selected list
       */
      selectedList: null,
      dataTableListItemId:"dataTableListItemId",
      paginationListItem: {
        rowsPerPage: this.getRowsFromLocalStorage("dataTableListItemId")
      },
      loadingDelete: false,
      permissionName: "list",

      /**
       * List of items for the list being edited
       */
      listItems: []
    };
  },

  computed: {
    /**
     * Headers for the list items table
     * @return {Array}
     */
    headers() {
      let headers = [
        {
          text: this.$i18n.tc("Code", 1),
          align: "left",
          value: "code",
          width: this.isCustomList ? "40%" : null
        },
        {
          text: this.$i18n.tc("Label", 1),
          align: "left",
          value: "libelle",
          width: this.isCustomList ? "40%" : null
        }
      ];
      if (!this.isCustomList) {
        headers.push(
          {
            text: this.$i18n.t("ClientCode"),
            align: "left",
            value: "client_code"
          },
          {
            text: this.$i18n.t("ClientLabel"),
            align: "left",
            value: "client_label"
          },
          {
            text: this.$i18n.t("Blocked"),
            align: "left",
            value: "blocked"
          },
          {
            text: this.$i18n.t("Hidden"),
            align: "left",
            value: "deleted"
          },
          {
            text: this.$i18n.t("Definition"),
            align: "left",
            value: "definition"
          }
        );
      }
      headers.push(
        {
          text: this.$i18n.t("Index"),
          align: "left",
          value: "index"
        },
        {
          text: "",
          align: "left",
          value: "action",
          sortable: false,
          width: "10%"
        }
      );
      return headers;
    },

    /**
     * Gets the pagination info for the table from the store
     * @return {Object}
     */
    paginationFromStore() {
      return this.$store.getters.getPaginationListInfo;
    },

    /**
     * Gets the search value from the store
     * @return {String}
     */
    searchFromStore() {
      return this.$store.getters.getSingleListSearch;
    },

    /**
     * Gets the list name from the route parameters
     * @return {String}
     */
    listName() {
      return this.$route.params.listName;
    },

    /**
     * Returns the items list depending of the page
     * If the page is for the creation of a new list, the newListItems list is returned
     * Else, the listItems list is returned
     */
    getItems() {
      if (this.$route.name === "custom-list-create") {
        return this.newListItems;
      } else {
        return this.listItems;
      }
    }
  },

  mounted() {
    this.fetchListByListName();
  },

  watch: {
    /**
     * When the computed property paginationFromStore changes, assigns its value to the variable pagination
     */
    paginationFromStore(newValue) {
      if (newValue) {
        this.pagination = newValue;
      }
    },

    /**
     * When the listItems variable changes, emits an event to update the listItems in the parent components
     */
    listItems() {
      this.$emit("updateListItems", this.listItems);
    }
  },

  beforeDestroy() {
    this.updatePaginationInStore(this.pagination);
  },

  methods: {
    ...mapActions([
      "GET_LIST",
      "DELETE_LIST_ITEM",
      "GET_LIST_OF_LISTS",
      "updateSingleListSearch",
      "updatePaginationListInfo",
      "deleteCustomListValue"
    ]),

    /**
     * Loads the list by its name and asks for the list values to be loaded
     */
    async fetchListByListName() {
      if (this.listName) {
        this.loading = true;
        try {
          let result = await this.GET_LIST_OF_LISTS();
          let lists = result.data.data;
          let code = this.listName.replace("list_", "l_");
          this.selectedList = lists.find(list => list.code === code);
          await this.getListValues();
        } catch (error) {
          console.error(error);
        } finally {
          this.loading = false;
          this.$nextTick(() => (this.pagination = this.paginationFromStore));
        }
      }
    },

    /**
     * Loads the list values and emits an event to udpdate the listItems variable in the parent component
     */
    async getListValues() {
      this.loading = true;
      try {
        let valuesList = await this.GET_LIST({
          listName: this.listName,
          code: [],
          includeDeleted: true
        });
        let filteredValuesList = [];
        for (const value of valuesList) {
          if (value.deleted_at === null) {
            filteredValuesList.push(value);
          }
        }
        this.listItems = filteredValuesList;
        this.$emit("updateListItems", filteredValuesList);
      } catch (error) {
        this.$router.replace("/error");
        console.log(error);
      } finally {
        this.loading = false;
      }
    },

    /**
     * Deletes the selected list item and reloads the list items
     * If the user is creating a new list it removes the item from the temporary list
     * Else, sends a request to the backend to delete the item
     */
    async deleteItem() {
      if (this.$route.name === "custom-list-create") {
        let index = this.listItems.findIndex(
          item => item.code === this.itemToDelete.code
        );
        this.listItems.splice(index, 1);
        this.deleteDialog = false;
      } else {
        try {
          let listName = this.selectedList.code.replace("l_", "list_");
          this.loadingDelete = true;

          if (this.isCustomList) {
            await this.deleteCustomListValue(this.itemToDelete.uuid);
          } else {
            let data = { listName: listName, itemCode: this.itemToDelete.code };
            await this.DELETE_LIST_ITEM(data);
          }

          this.itemToDelete = {};
          this.deleteDialog = false;
          await this.getListValues();
        } catch (error) {
          this.$toasted.global.toast_error("Cannot Delete Item");
          console.error(error);
        } finally {
          this.loadingDelete = false;
        }
      }
    },

    /**
     * Updates the search value in the store
     */
    updateSearchInStore(search) {
      this.updateSingleListSearch(search);
    },

    /**
     * Updates the pagination in the store
     */
    updatePaginationInStore(pagination) {
      this.updatePaginationListInfo(pagination);
    },

    /**
     * Redirects to the edit list item page
     */
    editItem(item) {
      let listName = this.$route.params.listName;
      this.$router.push({
        name: "list-settings-item-edit",
        params: {
          listName: listName,
          item: this.isCustomList ? item.uuid : item.code,
          isCustomList: this.isCustomList
        }
      });
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/SystemManagement/SystemSettings/ListsSettings/ListItemTable.vue